import { Injectable } from '@angular/core';
import { Reservation } from '../interfaces/reservation';
import { RestApiService } from './rest-api.service';
import * as moment from 'moment';
import { DateService } from './date.service';

@Injectable({
    providedIn: 'root'
})
export class ReservationsService {

    reservations: Array<Reservation> = [];
    reservationCount: number = 0;
    maxPages: number = 1;
    pageSize: number = 16;

    constructor(
        private api: RestApiService,
        private dateService: DateService
    ) { }

    getReservation(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get('/MI_Galago/WCF_MI_Galago/REST/vop/reservation/' + id, {}, true).then((res) => {
                resolve(res);
            });
        });
    };

    updateReservationCount(start, end, status, idAccount?: number): Promise<any> {
        return new Promise((resolve, reject) => {
            let query: any = {
                dateStart: start + 'T00:00:00.000Z',
                dateEnd: end + 'T23:59:59.999Z',
                status: status
            };
            if (typeof idAccount === 'number' && idAccount > 0) {
                query.idAccount = idAccount;
            }
            this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetReservationsVopCount', query, true).then((res) => {
                this.reservationCount = res;
                this.maxPages = Math.ceil(res / this.pageSize);
                if (res <= this.pageSize) {
                    this.maxPages = 0;
                }
                resolve(res);
            });
        });
    }

    getReservationMaxPages(): number {
        return this.maxPages;
    }

    getReservationCount(): number {
        return this.reservationCount;
    };

    setReservations(reservations): void {
        this.reservations = reservations;
    }

    getReservations(): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetReservationsVOP', {}, false).then((res) => {
                if (typeof res === 'object' && res.length > 0) {
                    this.reservations = res;
                }
                resolve(res);
            });
        });
    };

    deleteReservation(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.delete(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${id}`).then((res) => {
                resolve(res);
            });
        });
    };

    updateReservationByDate(start, end, page, status, search, societyCode): Promise<any> {
        return new Promise((resolve, reject) => {
            let query: any = {
                page: page,
                pageSize: this.pageSize,
                dateStart: start + 'T00:00:00.000Z',
                dateEnd: end + 'T23:59:59.999Z',
                status: status,
                sortColumn: 'idBooking',
                sortDirection: 'desc'
            };

            if (typeof search === 'string' && search.length > 0) {
                query.search = search;
            }
            if (typeof societyCode === 'string' && societyCode.length > 0) {
                query.societyCode = societyCode;
            }

            this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetReservationsVopCount', query, true).then((count) => {
                this.reservationCount = count;
                this.maxPages = Math.ceil(count / this.pageSize);
                if (count <= this.pageSize) {
                    this.maxPages = 0;
                }
                this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetReservationsVOP', query, true).then((res) => {
                    if (typeof res === 'object' && res.length > 0)
                        this.setReservations(res);
                    resolve(res);
                });
            });
        });
    }

    updateReservationByUser(
        idAccount: number,
        start: string,
        end: string,
        page: number
    ): Promise<any> {
        return new Promise((resolve, reject) => {
            let query: any = {
                page: page,
                pageSize: this.pageSize,
                dateStart: start + 'T00:00:00.000Z',
                dateEnd: end + 'T23:59:59.999Z',
                idAccount: idAccount
            };

            this.api.get('/MI_Galago/WCF_MI_Galago/REST/GetReservationsVOP', query, true).then((res) => {
                resolve(res);
            });
        });
    }

    updateReservationStatus(id, status): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.post(`/MI_Galago/WCF_MI_Galago/REST/vop/reservation/${id}/status`, {
                status: status
            }, false).then((res) => {
                resolve(res);
            });
        });
    }

    getVehicleReservation(vehicleName): Promise<any> {
        return new Promise((resolve, reject) => {
            let query: any = {
                page: 1,
                pageSize: 25,
                dateStart: moment().format().split('T')[0] + 'T00:00:00.000Z',
                dateEnd: moment().add(1, 'days').format().split('T')[0] + 'T23:59:59.999Z',
                status: 0,
                search: vehicleName
            };

            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/GetReservationsVOP`, query, true).then((res) => {
                resolve(res);
            })
        });
    }

    /* Checkout / Reservation dates */

    getReservationCheckout(id): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${id}/booking-checkout`, {}, false).then((res) => {
                resolve(res);
            });
        });
    }

    changeReservationPeriod(id: number, dateStart: string, dateEnd: string): Promise<any> {
        return new Promise((resolve, reject) => {
            let query: any = {
                dateStart: dateStart,
                dateEnd: dateEnd
            };

            this.api.post('/MI_Galago/WCF_MI_Galago/REST/vop/booking/' + id + '/extend', query, false).then((res) => {
                resolve(res);
            })
        });
    }

    changeReservationCheckout(id, checkout): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.put(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${id}/booking-checkout`, {
                bookingCheckout: checkout
            }, false).then((res) => {
                resolve(res);
            })
        });
    }

    updateReservationAmounts(id, amounts: {
        penalityAmount: number,
        penalityReason: string,
        discount: number
    }): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.post('/MI_Galago/WCF_MI_Galago/REST/vop/reservation/' + id + '/billing-amounts', amounts, false).then((res) => {
                resolve(res);
            });
        });
    }

    closeReservation(code, dateString): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.post(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/close`, {
                bookingCode: code,
                closingDate: dateString
            }).then((res) => {
                resolve(res);
            })
        });
    }

    /* Estimate mileage and hours */

    getEstimatedRealPeriod(idEquipment: number, start: string, end: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/equipment/${idEquipment}/estimated-real-period`, {
                startDate: start,
                endDate: end
            }, false).then((res) => {
                resolve(res);
            });
        });
    }

    getEstimatedMileage(idEquipment: number, start: string, end: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/equipment/${idEquipment}/pts-gps/mileage`, {
                startDate: start,
                endDate: end
            }, false).then((res) => {
                resolve(res);
            });
        });
    }

    /* Invoice related */

    getReservationInvoice(idReservation: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${idReservation}/booking-invoice/pdf`, {}, false).then((res) => {
                resolve(res);
            });
        });
    }

    getReservationInvoiceFields(idReservation: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${idReservation}/booking-invoice/booking-invoice-field`, {}, false).then((res) => {
                resolve(res);
            });
        });
    }

    getReservationInvoiceField(idInvoiceField: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/vop/booking-invoice/booking-invoice-field/${idInvoiceField}`, {}, false).then((res) => {
                resolve(res);
            });
        });
    }

    createReservationInvoiceField(invoiceField: {
        IdInvoiceField: number,
        IdInvoice: number,
        Title: string,
        Quantity: number,
        ExcludingTaxes: number,
        VAT: number,
        IncludingTaxes: number
    }): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.post(`/MI_Galago/WCF_MI_Galago/REST/vop/booking-invoice/booking-invoice-field`, {
                invoiceField: invoiceField
            }, false).then((res) => {
                resolve(res);
            });
        });
    }

    deleteReservationInvoiceField(idInvoiceField: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.delete(`/MI_Galago/WCF_MI_Galago/REST/vop/booking-invoice/booking-invoice-field/${idInvoiceField}`).then((res) => {
                resolve(res);
            });
        });
    }

    updateReservationInvoiceField(invoiceField: {
        IdInvoiceField: number,
        IdInvoice: number,
        Title: string,
        Quantity: number,
        ExcludingTaxes: number,
        VAT: number,
        IncludingTaxes: number
    }): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.put(`/MI_Galago/WCF_MI_Galago/REST/vop/booking-invoice/booking-invoice-field`, {
                invoiceField: invoiceField
            }, false).then((res) => {
                resolve(res);
            });
        });
    }

    createReservation(payload: {
        idStation: number,
        idVehicle: number,
        begin: string,
        end: string,
        comment?: string,
        packIds?: number[],
        idAccountTarget?: number
    }): Promise<any> {
        return new Promise((resolve, reject) => {
            const body: any = {
                idStation: payload.idStation,
                idVehicle: payload.idVehicle,
                begin: payload.begin,
                end: payload.end,
                comment: payload.comment || '',
                packIds: payload.packIds || []
            };
            if (typeof payload.idAccountTarget === 'number') {
                body.idAccountTarget = payload.idAccountTarget;
            }
            this.api.post('/MI_Galago/WCF_MI_Galago/REST/vop/booking', body, false).then((res) => {
                resolve(res);
            }).catch((err) => {
                reject(err);
            });
        });
    }

    getReservationEvents(idReservation: number): Promise<any> {
        return new Promise((resolve, reject) => {
            this.api.get(`/MI_Galago/WCF_MI_Galago/REST/vop/booking/${idReservation}/event`, {}, false).then((res) => {
                resolve(res);
            });
        });
    }
}
